import React, { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShoppingBag, BarChart3 } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { ProductCard } from "@/components/site/ProductCard";
import { buildOffers, effectivePrice, mvPrice } from "@/lib/compare";

const SORTS = {
  popular: () => 0,
  cashback: (a, b) => b.cashback_percent - a.cashback_percent,
  low: (a, b) => mvPrice(a) - mvPrice(b),
  high: (a, b) => mvPrice(b) - mvPrice(a),
  best: (a, b) => Math.min(...buildOffers(a).map(effectivePrice)) - Math.min(...buildOffers(b).map(effectivePrice)),
};

const CategoryChips = ({ categories, active }) => (
  <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="category-chips">
    {[{ id: "all", name: "All" }, ...categories].map((c) => {
      const slug = c.id === "all" ? null : c.name.toLowerCase();
      const isActive = (active || null) === slug;
      return (
        <Link key={c.id} to={slug ? `/products/${slug}` : "/products"} className={`shrink-0 h-10 px-4 inline-flex items-center rounded-full text-sm font-bold border-2 transition-colors ${isActive ? "bg-primary-600 border-primary-600 text-white" : "bg-white border-primary-100 text-primary-700 hover:border-primary-300"}`} data-testid={`chip-${slug || "all"}`}>
          {c.name}
        </Link>
      );
    })}
  </div>
);

const ProductsPage = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").toLowerCase();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(searchParams.get("brand") || "all");
  const [sort, setSort] = useState("popular");
  const [compare, setCompare] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams({ limit: "60" });
    if (category) params.append("category", category);
    if (brand !== "all") params.append("brand", brand);
    setLoading(true);
    Promise.all([axios.get(`${API}/products/affiliate?${params}`), axios.get(`${API}/categories`), axios.get(`${API}/brands`)])
      .then(([p, c, b]) => {
        setProducts(p.data.products || []);
        setCategories(c.data.categories || []);
        setBrands(b.data.brands || []);
      })
      .catch((err) => console.error("Failed to fetch products", err))
      .finally(() => setLoading(false));
  }, [category, brand]);

  const shown = products
    .filter((p) => !q || p.title.toLowerCase().includes(q) || p.brand_name.toLowerCase().includes(q))
    .sort(SORTS[sort]);
  const title = category ? category.charAt(0).toUpperCase() + category.slice(1) : q ? `Results for "${searchParams.get("q")}"` : "All deals";

  return (
    <div className="min-h-screen bg-background" data-testid="products-page">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-primary-900 tracking-tight" data-testid="products-title">{title}</h1>
            <p className="text-sm text-primary-500 mt-1" data-testid="products-count">{shown.length} products</p>
          </div>
          <label className="flex items-center gap-3 h-12 pl-4 pr-3 rounded-full bg-white border-2 border-primary-100 cursor-pointer" data-testid="compare-toggle-label">
            <BarChart3 className="w-4 h-4 text-primary-600" />
            <span className="text-sm font-bold text-primary-800">Compare prices</span>
            <Switch checked={compare} onCheckedChange={setCompare} data-testid="products-compare-toggle" />
          </label>
        </div>
        <CategoryChips categories={categories} active={category} />
        <div className="flex gap-3">
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="h-11 rounded-full bg-white border-2 border-primary-100 w-full sm:w-56" data-testid="brand-filter"><SelectValue placeholder="All brands" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brands</SelectItem>
              {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-11 rounded-full bg-white border-2 border-primary-100 w-full sm:w-56" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">Popular</SelectItem>
              <SelectItem value="cashback">Highest cashback</SelectItem>
              <SelectItem value="best">Best compared price</SelectItem>
              <SelectItem value="low">Price: low to high</SelectItem>
              <SelectItem value="high">Price: high to low</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-3xl bg-primary-100 animate-pulse" />)}
          </div>
        ) : shown.length ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="products-grid">
            {shown.map((p) => <ProductCard key={p.id} product={p} compare={compare} testIdPrefix="product" />)}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-3xl border-2 border-primary-100" data-testid="products-empty">
            <ShoppingBag className="w-14 h-14 text-primary-200 mx-auto mb-3" />
            <p className="font-heading text-xl font-bold text-primary-900">No products found</p>
            <Link to="/products" onClick={() => setBrand("all")} className="inline-block mt-4 px-5 h-10 leading-10 rounded-full bg-primary-600 text-white font-bold" data-testid="clear-filters-btn">Clear filters</Link>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
};

export default ProductsPage;
